import { useState } from "react";

import { getHeaders } from "../utils/headers";

type ApiResponse<T = any> = {
  isLoading: boolean;
  status?: number | null;
  error: string | null;
  data: T | null;
};

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type HTTPHeaders = {
  [key: string]: string;
};

export const useNetwork = () => {
  const [response, setResponse] = useState<ApiResponse>({
    isLoading: false,
    error: null,
    data: null,
  });

  const fetchData = async <T = any>(
    url: string,
    method: HttpMethod,
    headers?: HTTPHeaders,
    body?: any,
    isFormData?: boolean,
  ): Promise<ApiResponse<T>> => {
    setResponse({ ...response, isLoading: true });
    const host =
      url.match(/(?<=https:\/\/|http:\/\/)(.*?)(?=\/)/)?.[0] ??
      "qa1.foundit.in";
    const modifiedHeader = getHeaders(host, isFormData);

    try {
      const fetchOptions: RequestInit = {
        method,
        credentials: "include",
        headers: {
          ...modifiedHeader,
          ...headers,
        },
        body: isFormData ? body : JSON.stringify(body),
      };

      const fetchResponse = await fetch(url, fetchOptions);
      const responseData = await fetchResponse.json();

      if (!fetchResponse.ok) {
        const errorResponse: ApiResponse<T> = {
          isLoading: false,
          error: JSON.stringify(responseData),
          data: null,
          status: fetchResponse.status,
        };
        setResponse(errorResponse);
        return errorResponse;
      }

      const typedResponse: ApiResponse<T> = {
        isLoading: false,
        error: null,
        data: responseData,
        status: fetchResponse.status,
      };
      setResponse(typedResponse);
      return typedResponse;
    } catch (error: any) {
      const errorResponse: ApiResponse<T> = {
        isLoading: false,
        error: error?.message ?? "Something went wrong",
        data: null,
        status: 500,
      };
      setResponse(errorResponse);
      return errorResponse;
    }
  };

  return { response, fetchData };
};
